import React from 'react';
import {
  Autocomplete,
  Backdrop,
  Button,
  Fade,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ErrorOutline } from '@mui/icons-material';
import { Box } from '@mui/system';
import { IoWarningOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles()(theme => {
  return {
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    paper: {
      width: '70vw',
      height: '700px',
      border: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
      padding: '2rem 3rem',
    },
    form: {
      marginTop: theme.spacing(5),
      width: 574,
    },
    formControl: {
      width: '100%',
    },
    btns: {
      flex: '30%',
      borderRadius: 24,
      textTransform: 'unset',
    },
  };
});

const MapHeaders = ({
  open,
  closeHandler,
  headers,
  data,
  properties,
  setMappings,
  mappings,
  requiredHeaders,
  submit,
  fileType,
}) => {
  const { classes } = useStyles();
  const selectedValues = Object.values(mappings);
  const { t } = useTranslation();
  const handleMappingChange = (header, value) => {
    setMappings(prev => ({
      ...prev,
      [header]: value,
    }));
  };

  const allRequiredHeaderMatched = requiredHeaders.every(item =>
    selectedValues
      .map(x => x?.trim().toLowerCase())
      .includes(item.trim().toLowerCase())
  );
  return (
    <Modal
      className={classes.modal}
      open={open}
      onClose={closeHandler}
      closeAfterTransition
      slots={Backdrop}
      slotProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <Paper className={classes.paper}>
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={'10px'}
            alignSelf={'flex-start'}
          >
            <IoWarningOutline color='red' />
            <Typography>{t('mapHeaders.header')}</Typography>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('mapHeaders.column1')}</TableCell>
                  <TableCell>{t('mapHeaders.column2')}</TableCell>
                  <TableCell>{t('mapHeaders.column3')}</TableCell>
                  <TableCell>{t('mapHeaders.column4')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {headers.map((header, index) => {
                  const availableOptions = properties.filter(
                    prop =>
                      !selectedValues.includes(prop) ||
                      mappings[header] === prop
                  );
                  return (
                    <TableRow key={header}>
                      <TableCell>{header}</TableCell>
                      <TableCell>
                        {data
                          .slice(0, 3)
                          .map((row, i) =>
                            fileType === 'csv' ? (
                              <div key={i}>{row[index]}</div>
                            ) : (
                              <div key={i}>{row[header]}</div>
                            )
                          )}
                      </TableCell>
                      <TableCell>
                        {mappings[header] ? (
                          <CheckCircleOutlineIcon color='success' />
                        ) : (
                          <ErrorOutline color='warning' />
                        )}
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          options={availableOptions.map(option => ({
                            label: t(`mapHeaders.properties.${option}`),
                            value: option,
                          }))}
                          renderInput={params => (
                            <TextField {...params} label={''} />
                          )}
                          renderOption={(props, option) => {
                            const isRequired = requiredHeaders.find(
                              head => head === option.value
                            );
                            return (
                              <li {...props} data-hook={option.value}>
                                {option.label}
                                {isRequired && <span>*</span>}
                              </li>
                            );
                          }}
                          getOptionLabel={option => option.label}
                          isOptionEqualToValue={(option, value) =>
                            option.value === value.value
                          }
                          onChange={(e, newValue) =>
                            handleMappingChange(header, newValue?.value)
                          }
                          value={
                            mappings[header]
                              ? {
                                  label: t(
                                    `mapHeaders.properties.${mappings[header]}`
                                  ),
                                  value: mappings[header],
                                }
                              : null
                          }
                          data-hook={`properties-${index}`}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Box display={'flex'} alignSelf={'flex-end'} gap={'10px'}>
            <Button variant='outlined' onClick={closeHandler}>
              {t('common.cancel')}
            </Button>
            <Button
              variant='contained'
              disabled={!allRequiredHeaderMatched}
              onClick={submit}
              data-hook='confirm-btn'
            >
              {t('common.confirm')}
            </Button>
          </Box>
          <span>{t('mapHeaders.footer')}</span>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default MapHeaders;
